import { AbstractControl, ValidatorFn } from '@angular/forms';

/**
 *Validates that at least one program is selected in a form control group.
 *
 *@description
 *This validator checks if at least one value in the control's value object is true,
 *indicating that at least one program has been selected. If no programs are selected,
 *it returns an error object with the key 'noPrograms'.
 *
 *@example
 *```typescript
 *this.formGroup = this.fb.group({
 *  programs: this.fb.group({
 *    ccm: [false],
 *    pcm: [false],
 *    tcm: [false]
 *  }, { validators: programsValidator })
 *});
 *```
 *
 *@returns A validator function that returns null if valid, or an error object if invalid
 */
export const programsValidator: ValidatorFn = (control: AbstractControl) => {
  const values = Object.values(control.value);
  const isValid = values.some((value) => value === true);
  return isValid ? null : { noPrograms: true };
};

/**
 *Validates that APCM program is not selected alongside other care management programs.
 *
 *@description
 *This validator checks if APCM (Advanced Primary Care Management) is selected along with
 *any other care management program (CCM, PCM, or TCM). If APCM is selected alongside
 *any of these programs, it returns an error object with the key 'apcmConflict'.
 *
 *@example
 *```typescript
 *this.formGroup = this.fb.group({
 *  programs: this.fb.group({
 *    apcm: [false],
 *    ccm: [false],
 *    pcm: [false],
 *    tcm: [false]
 *  }, { validators: [programsValidator, apcmValidator] })
 *});
 *```
 *
 *@returns A validator function that returns null if valid, or an error object if invalid
 */
export const apcmValidator: ValidatorFn = (control: AbstractControl) => {
  const hasAPCM = Object.keys(control.value).some((key) => key.startsWith('apcm')) && control.value?.apcm;
  const hasCCM = Object.keys(control.value).some((key) => key.startsWith('ccm')) && control.value?.ccm;
  const hasPCM = Object.keys(control.value).some((key) => key.startsWith('pcm')) && control.value?.pcm;
  const hasTCM = Object.keys(control.value).some((key) => key.startsWith('tcm')) && control.value?.tcm;
  if (hasAPCM) {
    return hasCCM || hasPCM || hasTCM ? { apcmConflict: true } : null;
  }
  return null;
};

export const onHoldDateRequiredValidator: ValidatorFn = (control: AbstractControl) => {
  const label = control.get('label')?.value;
  const dateOnHold = control.get('dateOnHold')?.value;
  if (label === 'Active - On Hold' && !dateOnHold) {
    return { dataRequired: true };
  }
  return null;
};

/**
 * Blocks save until an explicit inactive status is chosen once a patient has no
 * active programs left (the "forced inactive choice" UX).
 *
 * @description
 * Only engages when `enabled` is true (the status catalog is present) and the patient has no
 * active programs. In that case the status `label` must be one of the allowed inactive labels;
 * otherwise it returns `inactiveChoiceRequired`. Forcing the choice is always mandatory — there is
 * no feature flag; `enabled` just guards against running before the catalog has loaded. When the
 * patient still has an active program it is a no-op. Context is read lazily via `getContext`
 * because the catalog loads asynchronously after the form is built.
 *
 * @returns A validator function that returns null if valid, or an error object if invalid
 */
export const forcedInactiveChoiceValidator = (
  getContext: () => { enabled: boolean; allowedLabels: string[]; hasActivePrograms: boolean }
): ValidatorFn => {
  const validate: ValidatorFn = (control: AbstractControl) => {
    const { enabled, allowedLabels, hasActivePrograms } = getContext();
    if (!enabled || hasActivePrograms) {
      return null;
    }
    const label = control.get('label')?.value;
    return allowedLabels.includes(label) ? null : { inactiveChoiceRequired: true };
  };
  return validate;
};
